<template>
    <div class="col-12">
        <div v-if="!isLoading" class="row fade-in">
            <div class="col-12 d-flex justify-content-end">
                <b-spinner v-if="isSequenceLoading" class="mt-2" variant="info" small />
            </div>
            <div class="col-12">
                <div class="py-2">
                    <draggable
                        v-model="tableRows"
                        handle=".group-drag-handle"
                        ghost-class="drag-ghost"
                        chosen-class="drag-chosen"
                        drag-class="drag-active"
                        :animation="300"
                        easing="cubic-bezier(0.25, 1, 0.5, 1)"
                        :disabled="!status.save || isSequenceLoading"
                        @end="updateSequenceRequest"
                    >
                        <div v-for="group in tableRows" :key="group.uuid" class="d-flex align-items-start mb-3">
                            <div class="mr-2 pt-3">
                                <i class="mdi mdi-menu font-size-18 cursor-pointer group-drag-handle text-muted"></i>
                            </div>
                            <div class="card border border-secondary shadow-sm flex-grow-1 mb-0">
                                <div class="card-header bg-light py-2 d-flex align-items-center justify-content-between">
                                    <span class="font-weight-bold text-break"><i class="mdi mdi-folder-outline text-info mr-2"></i>{{ group.name }}</span>
                                    <div class="text-nowrap ml-3">
                                        <b-button type="button" variant="link" class="text-info p-0 mr-1" :disabled="!status.save || isSequenceLoading" :title="$t(expandedGroups.includes(group.uuid) ? 'label.hideX' : 'label.viewX', [$t('module.department')])" v-b-tooltip.hover="{ boundary: 'window' }" @click="toggleGroup(group.uuid)">
                                            <i :class="['mdi font-size-18', expandedGroups.includes(group.uuid) ? 'mdi-chevron-up' : 'mdi-chevron-down']"></i>
                                        </b-button>
                                        <b-button type="button" variant="link" class="text-info p-0 mr-1" :disabled="!status.save || isSequenceLoading" :title="$t('label.updateX', [])" v-b-tooltip.hover="{ boundary: 'window' }" @click="openForm(group)">
                                            <i class="mdi mdi-pencil font-size-18"></i>
                                        </b-button>
                                        <b-button type="button" variant="link" class="text-danger p-0" :disabled="!status.save || isSequenceLoading" :title="$t('label.deleteX', [])" v-b-tooltip.hover="{ boundary: 'window' }" @click="deleteConfirmation(group)">
                                            <i class="mdi mdi-delete-outline font-size-18"></i>
                                        </b-button>
                                    </div>
                                </div>
                                <div v-if="expandedGroups.includes(group.uuid)" class="card-body p-0">
                                    <PhoneExtDepartmentSortable :groupUUID="group.uuid" />
                                </div>
                            </div>
                        </div>
                    </draggable>
                    <div class="d-flex align-items-center justify-content-center">
                        <b-button
                            type="button"
                            variant="outline-info"
                            class="btn-sm border-dashed border-info flex-grow-1 ml-4 cursor-pointer"
                            :disabled="!status.save || isSequenceLoading"
                            @click="openForm()"
                        >
                            <i class="mdi mdi-plus-circle-outline mr-2"></i>
                            <small>{{ $t('label.addX', [$t('label.section')]) }}</small>
                        </b-button>
                    </div>
                </div>
            </div>
        </div>
        <div v-else class="d-flex justify-content-center align-items-center m-5 p-5">
            <b-spinner class="m-5" variant="info" role="status" />
        </div>
        <b-modal
            :id="formModalId"
            :title="$t(group.uuid ? 'label.updateX' : 'label.createX', [$t('module.group')])"
            @hidden="closeForm"
            hide-footer
            hide-header-close
            centered
            :no-close-on-backdrop="!status.save"
            :no-close-on-esc="!status.save"
            size="lg"
        >
            <b-container fluid>
                <PhoneExtGroupForm
                    v-if="showForm"
                    :phoneExtUUID="phoneExtUUID"
                    :groupUUID="group.uuid"
                    :sequence="group.sequence"
                    @done="closeForm"
                    @reload="getGroupList"
                />
            </b-container>
        </b-modal>
        <b-modal :id="deleteModalId" :title="$t('label.deleteX', [$t('module.group')])" hide-header-close :no-close-on-backdrop="!status.save" :no-close-on-esc="!status.save">
            <p class="my-4">{{ $t('message.deleteConfirmation') }}</p>
            <template #modal-footer>
                <b-button variant="danger" :disabled="!status.save" @click="$bvModal.hide(deleteModalId)">{{ $t('label.cancel') }}</b-button>
                <b-button variant="info" :disabled="!status.save" @click="requestDelete">
                    <span v-if="status.save">{{ $t('label.deleteX', []) }}</span>
                    <b-spinner v-else small />
                </b-button>
            </template>
        </b-modal>
    </div>
</template>
<script>
import PhoneExtDepartmentSortable from '@/views/pages/admin/phoneExtension/phoneExtensionDepartment/phoneExtDepartmentSortable';
import draggable from 'vuedraggable';
import { mapState } from 'vuex';
import { phoneExtGroupMethods } from '@/state/api';
import { PHONE_EXT_GROUP_MODULE } from '@/state/api/phoneExtGroup.state';
import PhoneExtGroupForm from '@/views/pages/admin/phoneExtension/phoneExtentionGroup/phoneExtGroupForm';

export default {
    props: {
        phoneExtUUID: { type: String, required: true },
    },
    components: { draggable, PhoneExtGroupForm, PhoneExtDepartmentSortable },
    created() {
        this.getGroupList();
    },
    data() {
        return {
            isSequenceLoading: false,
            isLoading: true,
            showForm: false,
            expandedGroups: [],
            group: { uuid: '', sequence: 1 },
        };
    },
    methods: {
        ...phoneExtGroupMethods,
        toggleGroup(uuid) {
            const index = this.expandedGroups.indexOf(uuid);
            if (index == -1) this.expandedGroups.push(uuid);
            else this.expandedGroups.splice(index, 1);
        },
        getGroupList() {
            this.isLoading = true;
            return this.getAllPhoneExtGroup({filter: {phoneExtUuid: this.phoneExtUUID, isActive: 1}})
                .then((result) => {
                    this.isLoading = false;
                    return result;
                });
        },
        updateSequenceRequest(event) {
            if (event.oldIndex == event.newIndex) return;
            this.isSequenceLoading = true;
            let data = {
                phoneExtUuid: this.phoneExtUUID,
                groups: this.tableRows.map((item, index) => ({uuid: item.uuid, sequence: index + 1})),
            };
            return this.updatePhoneExtGroupSequence({data})
                .then(() => this.getGroupList())
                .then(() => { this.isSequenceLoading = false; });
        },
        openForm(group) {
            if (!this.status.save || this.isSequenceLoading) return;
            this.group = group?.uuid ? {uuid: group.uuid, sequence: group.sequence} : {
                uuid: '',
                sequence: Math.max(0, ...this.tableRows.map(item => Number(item.sequence) || 0)) + 1,
            };
            this.showForm = true;
            this.$nextTick(() => this.$bvModal.show(this.formModalId));
        },
        closeForm() {
            this.showForm = false;
            this.group = { uuid: '', sequence: 1 };
            this.$bvModal.hide(this.formModalId);
        },
        deleteConfirmation(group) {
            this.group = {uuid: group.uuid, sequence: group.sequence};
            this.$bvModal.show(this.deleteModalId);
        },
        requestDelete() {
            if (!this.status.save) return;
            let data = {uuid: this.group.uuid, isActive: 0};
            return this.updatePhoneExtGroupStatus({data})
                .then((result) => {
                    if (result?.success) {
                        this.$bvModal.hide(this.deleteModalId);
                        return this.getGroupList();
                    }
                });
        },
    },
    computed: {
        ...mapState(PHONE_EXT_GROUP_MODULE, ['status']),
        formModalId() {
            return `phone-ext-group-form-${this.phoneExtUUID}`;
        },
        deleteModalId() {
            return `phone-ext-group-delete-${this.phoneExtUUID}`;
        },
        tableRows: {
            get() {
                const groups = this.$store.state.api.phoneExtGroup.listByPhoneExt[this.phoneExtUUID] || [];
                return groups.filter(item => item.isActive)
                    .map(item => ({...item}))
                    .sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
            },
            set(value) {
                this.$store.commit(`${PHONE_EXT_GROUP_MODULE}/setPhoneExtGroupByPhoneExt`, {
                    phoneExtUuid: this.phoneExtUUID,
                    data: value.map((item, index) => ({...item, sequence: index + 1})),
                });
            },
        },
    },
};
</script>
<style scoped>
.group-drag-handle {
    cursor: grab;
}
.border-dashed {
    border-style: dashed !important;
}
</style>
